import { registerLocaleData } from '@angular/common';
import { isDevMode } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { fetchTranslations } from '@rxap/browser-localize';
import { applyThemeColorBootstrapHook } from '@rxap/browser-theme';
import { StandaloneApplication } from '@rxap/ngx-bootstrap';
import { loadI18nBootstrapHook, LOCALE_STORAGE_KEY } from '@rxap/ngx-localize';
import { OpenApiInit } from '@rxap/open-api';
import { UnregisterServiceWorker } from '@rxap/service-worker';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { resolveInitialLocale } from './app/shared/resolve-initial-locale';
import { environment } from './environments/environment';
import { initPosthog } from './app/shared/init-posthog';
// Local hook, not @rxap/ngx-sentry's — that package version pushes
// replayIntegration/feedbackIntegration unconditionally, ignoring the
// `sentry.integrations.{replay,feedback}` config gate. This copy respects it.
import { sentryInitBootstrapHook } from './app/shared/sentry-init-bootstrap-hook';

if ('__APP_ENVIRONMENT__' in window) {
  const customEnvironment = window['__APP_ENVIRONMENT__'];
  if (customEnvironment && typeof customEnvironment === 'object') {
    console.debug('Load custom environment', customEnvironment);
    Object.assign(environment, customEnvironment);
  } else {
    console.warn('The property __APP_ENVIRONMENT__ must be an object');
  }
} else {
  console.debug('No custom environment found. Use the default environment');
}

if (environment.sentry) {
  const originalBeforeSend = (environment.sentry as any).beforeSend;
  (environment.sentry as any).beforeSend = (event: any, hint: any) => {
    const storedConsentStr = localStorage.getItem('privacy-consent');
    const consent = storedConsentStr ? JSON.parse(storedConsentStr) : null;
    if (consent?.sentry) {
      return originalBeforeSend ? originalBeforeSend(event, hint) : event;
    }
    return null;
  };
}

async function loadLocaleDateModule(locale: string) {
  try {
    // Load required locale module (needs to be adjusted for different locales)
    let module: { default: any } | null = null;
    switch (locale.split('-')[0]) {
      default:
      case 'en':
        module = await import('@angular/common/locales/en');
        break;

      case 'de':
        module = await import('@angular/common/locales/de');
        break;

      case 'fr':
        module = await import('@angular/common/locales/fr');
        break;

      case 'it':
        module = await import('@angular/common/locales/it');
        break;

      case 'pl':
        module = await import('@angular/common/locales/pl');
        break;

      case 'nl':
        module = await import('@angular/common/locales/nl');
        break;

      case 'es':
        module = await import('@angular/common/locales/es');
        break;
    }
    if (module) {
      registerLocaleData(module.default);
    }
  } catch (e: any) {
    console.error(
      `Could not load locale module for locale ${locale}: ${e.message}`
    );
  }
}

const application = new StandaloneApplication(
  environment,
  AppComponent,
  appConfig,
  {
    fromDns: environment.fromDns,
    dnsServers: environment.dnsServers,
    ipfsGatewayServers: environment.ipfsGatewayServers,
    url: [
      'config.json'
    ],
  }
);
if (environment.serviceWorker) {
  application.before(() => UnregisterServiceWorker(environment));
}
application.before(() => OpenApiInit(environment));
application.before(
  sentryInitBootstrapHook(environment, (options) => ({
    ...options,
    beforeSend: (event) => {
      const storedConsentStr = localStorage.getItem('privacy-consent');
      try {
        const consent = storedConsentStr ? JSON.parse(storedConsentStr) : null;
        if (consent?.sentry) {
          return event;
        }
      } catch (e: any) {
        console.error(`Could not parse privacy consent: ${e.message}`);
      }
      return null;
    },
  }))
);
// Resolve the bootstrap locale against the deployment's supported languages
// (config i18n block) instead of persisting the raw region-tagged
// navigator.language — the raw value never matches a supported base key,
// which armed appLocalGuard's mid-navigation location.reload() on the first
// loaded archive (losing the in-memory container).
application.before(async function (config, options) {
  const languages = config.get<Record<string, string>>('i18n.languages', {
    en: 'English',
  });
  const defaultLanguage = config.get<string>('i18n.defaultLanguage', 'en');
  const locale = resolveInitialLocale({
    stored: localStorage.getItem(LOCALE_STORAGE_KEY),
    browserLanguages: navigator.languages ?? [navigator.language],
    supported: Object.keys(languages ?? {}),
    defaultLanguage,
  });
  return loadI18nBootstrapHook(
    fetchTranslations,
    loadLocaleDateModule,
    locale
  ).call(this, config, options);
});
if (!isDevMode()) {
  application.after(initPosthog);
}
application.before(applyThemeColorBootstrapHook());

application.after((app, config) => {
  const title = config.get('app.title');
  const description = config.get('app.description');
  if (title) {
    app.injector.get(Title).setTitle(title);
  }
  if (description) {
    app.injector
      .get(Meta)
      .addTag({ name: 'description', content: description });
  }
});

application.bootstrap().catch((err) => console.error(err));
